/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useContext } from 'react';
import {
    array, bool, func, object, string,
} from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Grid } from '@material-ui/core';

import { useSelector } from 'react-redux';
import { getBaseCode } from '../../../../../helpers/tracking/product/dataLayerProductHelpers';
import { getProductUnAvailable } from '../../../../../../state/ducks/App/App-Selectors';
import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import useIsPlaPage from '../../../../../helpers/hooks/useIsPlaPage';
import { CrossMerch } from '../../../CrossMerchContainer/CrossMerchContainer';
import ProductReview from '../ProductReview';

const useStyles = makeStyles((theme) => ({

    productSku: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '14px',
        lineHeight: '18px',
        textTransform: 'capitalize',
        color: theme.palette.upsellsTextColor || '#2F2F2F',
        width: '30%',
        [theme.breakpoints.down('xs')]: {
            marginTop: '10px',
        },
    },
    productPrice: {
        fontStyle: 'normal',
        fontWeight: 'bold',
        fontSize: '24px',
        lineHeight: '30px',
    },
    productQuickView: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '14px',
        lineHeight: '18px',
        textDecorationLine: 'underline',
        color: theme.palette.upsellsTextColor || '#2F2F2F',
        cursor: 'pointer',
        paddingTop: '0px',
    },
    shipmentMessage: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '14px',
        lineHeight: '18px',
        textTransform: 'capitalize',
        color: theme.palette.upsellsTextColor || '#2F2F2F',
        textDecoration: 'none',
        '@media (max-width: 768px) and (min-width: 600px)': {
            marginTop: '5px',
        },
        [theme.breakpoints.down('xs')]: {
            marginTop: '10px',
        },
    },
    shipmentMessageFuture: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '14px',
        lineHeight: '18px',
        textTransform: 'capitalize',
        color: theme.palette.upsellsFutureAvailabilityColor || '#A2011A',
        textDecoration: 'none',
    },
    desktopMessage: {
        display: 'flex',
    },
    priceContainer: {
        fontStyle: 'normal',
        fontWeight: 'bold',
        fontSize: '20px',
        lineHeight: '24px',
        display: 'flex',
        color: theme.palette.upsellsTextColor || '#000000',
        marginTop: '10px',
        '@media (min-width: 1280px)': {
            fontSize: '24px',
            lineHeight: '30px',
        },
    },
    crossedOutRetailPrice: {
        textDecoration: 'line-through',
        fontWeight: '400',
    },
    salePrice: {
        color: theme.palette.pdp?.salePrice || '#a1001a',
        margin: '0 0 0 10px',
    },
    selectedSalePrice: {
        fontWeight: 'bold',
    },
    discountAmount: {
        color: theme.palette.cms?.skuPriceDiscountColor,
        fontWeight: 'normal',
        marginLeft: '10px',
        fontSize: '0.8em',
    },
    selectedRetailPrice: {
        fontWeight: 'bold',
        color: theme.palette.common?.black,
        fontSize: '0.9em',
    },
    retailPrice: {
        fontSize: '0.9em',
        color: theme.palette.common?.black,
    },
    viewPriceBox: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        position: 'relative',
    },
    outOfStock: {
        color: theme.palette.cms?.red || '#ff0000',
        position: 'relative',
        alignItems: 'center',
        textAlign: 'center',
        fontSize: 15,
        display: 'flex',
        minWidth: '100%',
        marginLeft: '3rem',
        [theme.breakpoints.down(600)]: {
            marginLeft: '2rem',
        },
    },
    plaShipmentMessage: {
        marginTop: '0px',
    },
    priceWrapper: {
        '& $priceContainer': {
            fontSize: '28px',
            lineHeight: '36px',
            letterSpacing: '0.5px',
            paddingBottom: '12px',
            width: '100%',
        },
        '& $productSku': {
            fontSize: '16px',
            lineHeight: '25px',
            letterSpacing: '0.5px',
            marginBottom: '4px',
        },
        '& $gridContainer': {
            maxWidth: '100%',
            flexBasis: '100%',
            display: 'flex',
            alignItems: 'center',
        },
    },
    foodWrapper: {
        '& $priceContainer': {
            fontSize: '27px',
            lineHeight: '37px',
            paddingBottom: '4px',
            marginTop: '4px',
        },
    },
    gridContainer: {},
    plaRedesignWrapper: {
        '@media screen and (max-width:600px)': {
            '& $productSku': {
                margin: '4px 0px',
            },
        },
        '& $priceContainer': {
            margin: '0px',
            padding: '0px',
            fontSize: '22px',
            lineHeight: '27px',
            '@media screen and (max-width:600px)': {
                fontSize: '20px',
                lineHeight: '25px',
                marginBottom: '20px',
            },
        },
    },
}));

const ProductPriceName = ({
    pricesList,
    product,
    sku,
    getRetailPrice,
    getSalePrice,
    isPdpRedesignEnabled,
    selectedSku,
    productUrl,
    isPlaRedesignEnabled,
}) => {
    const classes = useStyles();
    const {  presentation_family: presentationFamily = '' } = useContext(CrossMerch);
    const productUnAvailable = useSelector(getProductUnAvailable);
    const foodPlaRedesign = useSelector(getFeatureFlag('which-food-pla-design'));
    const isAvailabilityAtcLeftAlign = useSelector(getFeatureFlag('is-availability-atc-left-aligned'));
    const isFood = presentationFamily === 'food';
    const isPlaPage = useIsPlaPage() && useSelector(getFeatureFlag('is-simplified-pdp')) && foodPlaRedesign === 'variant';
    const isPDPReviewsEnabled = useSelector(getFeatureFlag(['is-pdp-reviews-enabled']));
    const isPowerReviewEnabled = useSelector(getFeatureFlag(['is-power-review-enabled']));
    const isPDPYotpoEnabled = useSelector(getFeatureFlag(['is-pdp-yotpo-enabled']));
    const renderPrice = (itemSku, showPriceOff, skuDynamicPrice) => {
        const round = (value) => Number(`${Math.round(value * 10) / 10}`).toFixed(2);
        const retailPrice = getRetailPrice(skuDynamicPrice) > 0 ? getRetailPrice(skuDynamicPrice) : itemSku?.prices?.find((priceData) => priceData.type === 'retail')?.value;
        const salePrice = getSalePrice(skuDynamicPrice) > 0 ? getSalePrice(skuDynamicPrice) : itemSku?.prices?.find((priceData) => priceData.type === 'sale')?.value;
        const defaultPrice = `$${parseFloat(retailPrice).toFixed(2)}`;
        const offPrice = `${round(retailPrice - salePrice)} off` || '';
        return (
            <Grid item xs={12} className={classes.setItemLayout}>
                {salePrice && retailPrice && salePrice !== retailPrice
                    ? (
                        <>
                            <s regularprice="">
                                ${retailPrice}
                            </s>
                            <span className={classes.salePrice} saleprice="">
                                ${salePrice}
                            </span>
                            {showPriceOff && (
                                <span className={classes.offPrice} discount="">
                                    ${offPrice}
                                </span>
                            )}

                        </>
                    )
                    : <span className={classes.defaultPrice} productprice=""> {defaultPrice}</span>}
            </Grid>
        );
    };

    const isShipNow = product?.availability?.deliveryMessage === 'AVAILABLE TO SHIP NOW';
    const foodContainer = isPdpRedesignEnabled && isFood ? classes.foodWrapper : '';
    return (
        <Grid container className={`${isPdpRedesignEnabled ? classes.priceWrapper : ''} ${isPlaRedesignEnabled ? classes.plaRedesignWrapper : ''} ${foodContainer}`}>
            <Grid item xs={12}>
                <Grid container>
                    {isPdpRedesignEnabled && (
                        <div className={classes.viewPriceBox}>
                            <div className={classes.priceContainer}>{renderPrice(sku, false, pricesList)}
                                {productUnAvailable && <div className={classes.outOfStock}>Out Of Stock</div>}
                            </div>
                        </div>
                    )}
                    <Grid className={classes.gridContainer} item lg={2} md={2} sm={isPlaPage ? 2 : 12} xl={2} xs={3}>
                        { isPDPReviewsEnabled && isPowerReviewEnabled && isPdpRedesignEnabled && (
                            <ProductReview
                                product={product}
                                selectedSku={selectedSku}
                                productUrl={productUrl} // comes off content from 2 levels up
                                skuEnabledOnMobile
                                isStandardPdp
                                isPdpRedesignEnabled={isPdpRedesignEnabled}
                            />
                        )}
                        {!isPdpRedesignEnabled && <div className={classes.productSku}>{getBaseCode(sku.id, true)}</div>}
                    </Grid>
                    {!isPdpRedesignEnabled && ((!isAvailabilityAtcLeftAlign && isFood) || !isFood
                        ? (
                            <Grid item lg={10} md={10} sm={isPlaPage ? 10 : 12} xl={10} xs={9}>
                                <div className={`${classes.shipmentMessage} ${!isShipNow ? classes.shipmentMessageFuture : ''}  ${isPlaPage ? classes.plaShipmentMessage : ''}`} availability="">{product?.availability?.deliveryMessage?.toLowerCase?.()}</div>
                            </Grid>
                        ) : '')}
                </Grid>

                {!isPdpRedesignEnabled  && (
                    <div className={classes.viewPriceBox}>
                        <div className={classes.priceContainer}>{renderPrice(sku, false, pricesList)}
                            {productUnAvailable && <div className={classes.outOfStock}>Out Of Stock</div>}
                        </div>
                    </div>
                )}
            </Grid>
        </Grid>
    );
};

ProductPriceName.propTypes = {
    pricesList: array,
    product: object,
    sku: object,
    getRetailPrice: func,
    getSalePrice: func,
    isPdpRedesignEnabled: bool,
    selectedSku: object,
    productUrl: string,
    isPlaRedesignEnabled: bool,
};

ProductPriceName.defaultProps = {
    pricesList: [],
    product: {},
    sku: {},
    getRetailPrice: () => { },
    getSalePrice: () => { },
    isPdpRedesignEnabled: false,
    selectedSku: {},
    productUrl: '',
    isPlaRedesignEnabled: false,
};

export default ProductPriceName;
